import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "./cityPricing";

const saintRaphael: ChauffeurPage = {
  slug: "saint-raphael",
  title: "Chauffeur privé à Saint-Raphaël – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Saint-Raphaël : transferts aéroport de Nice (~1 h), navettes gare TGV, mise à disposition entre le port et l’Estérel, dans le Var, 7j/7.",
  keywords: [
    "chauffeur privé saint-raphaël",
    "vtc saint-raphaël",
    "vtc saint-raphael estérel",
    "transfert saint-raphaël aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Saint-Raphaël",
    subtitle:
      "Station balnéaire de l’Estérel et porte TGV du golfe : transferts aéroport de Nice (~1 h), navettes gare et mise à disposition, 24h/24.",
    keywords: [
      "VTC Saint-Raphaël",
      "chauffeur Estérel",
      "gare TGV Saint-Raphaël",
      "transfert aéroport Nice",
    ],
    imageSrc: heroImage.src,
    imageAlt:
      "Chauffeur privé & VTC à Saint-Raphaël, station balnéaire de l’Estérel",
    badges: [
      "Aéroport de Nice ~1 h",
      "Gare TGV Saint-Raphaël",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Saint-Raphaël",
    items: [
      {
        title: "Porte TGV du golfe",
        description:
          "La gare de Saint-Raphaël-Valescure relie Paris et Marseille en TGV : nous assurons vos navettes gare ⇄ hôtel et vos correspondances vers tout le Var.",
      },
      {
        title: "Aéroport de Nice sans conduire",
        description:
          "Environ 1 h par l’A8 jusqu’à l’aéroport de Nice Côte d’Azur, suivi de vol et accueil compris — plus loin que Cannes, on anticipe la marge.",
      },
      {
        title: "L’Estérel et ses criques",
        description:
          "Corniche d’Or, Cap Dramont, Agay, Anthéor : le chauffeur connaît la route rouge du massif et ses points de vue.",
      },
      {
        title: "Ports & plaisance",
        description:
          "Vieux-Port et Port Santa Lucia, l’un des plus grands de la côte : liaisons équipages et invités, note 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Saint-Raphaël",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Saint-Raphaël ⇄ aéroport de Nice Côte d’Azur en ~1 h par l’A8, avec suivi de vol et marge anticipée.",
      },
      {
        title: "Navette gare TGV",
        text: "Prise en charge à la gare de Saint-Raphaël-Valescure et liaison vers votre hôtel, une villa ou une autre ville du golfe.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée le long de la Corniche d’Or, entre plages, port et arrière-pays.",
      },
      {
        title: "Estérel & criques",
        text: "Cap Dramont, plage du Débarquement, Agay et l’Île d’Or : dépose aux départs de sentiers et aux clubs de plongée.",
      },
      {
        title: "Longue distance depuis Cannes/Nice",
        text: "Trajets depuis Cannes, Nice ou l’aéroport vers Saint-Raphaël et le golfe, en berline, van ou minibus VIP.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Vieux-Port & front de mer",
        description:
          "Casino, basilique Notre-Dame-de-la-Victoire et promenade.",
      },
      {
        name: "Gare de Saint-Raphaël-Valescure",
        description:
          "Gare TGV au cœur de la ville, correspondances vers tout le golfe.",
      },
      {
        name: "Port Santa Lucia",
        description: "Grande marina, départ des balades en mer.",
      },
      {
        name: "Corniche d’Or & Cap Dramont",
        description: "Route rouge de l’Estérel vers Agay et Anthéor.",
      },
      {
        name: "Valescure",
        description: "Quartier résidentiel et golf, à l’écart du front de mer.",
      },
      {
        name: "Aéroport de Nice",
        description: "Terminaux 1 et 2, à ~1 h par l’A8.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur au cœur de l’Estérel",
    subheading:
      "Station balnéaire, gare TGV et massif rouge : Saint-Raphaël est la porte d’entrée du golfe.",
    paragraphs: [
      "Adossée au massif de l’Estérel et posée face à la mer, Saint-Raphaël est la porte d’entrée du golfe de Fréjus : station balnéaire, grand port de plaisance et surtout gare TGV qui relie Paris et Marseille. KS Rent Services y assure transferts, navettes gare et mises à disposition avec chauffeur privé, à l’année.",
      "L’aéroport de Nice Côte d’Azur est à environ une heure par l’A8 : plus loin que depuis Cannes, ce trajet demande d’anticiper la marge, ce que nous intégrons systématiquement avec le suivi de vol. La gare de Saint-Raphaël-Valescure sert aussi de point de transfert pratique pour rejoindre le reste du Var.",
      "Sur place, le chauffeur suit la Corniche d’Or au fil du porphyre rouge — Cap Dramont, plage du Débarquement, Agay, Anthéor — et vous dépose aux criques, aux clubs de plongée ou aux départs de sentiers, avant une reprise à l’heure convenue.",
    ],
    bullets: [
      "Aéroport de Nice en ~1 h",
      "Navette gare TGV Saint-Raphaël",
      "Corniche d’Or & criques de l’Estérel",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Saint-Raphaël",
    steps: [
      "Indiquez votre trajet (aéroport, gare TGV, port, Estérel) et vos horaires.",
      "Nous confirmons le véhicule, la marge pour l’aéroport de Nice et le forfait.",
      "Le chauffeur suit votre vol ou votre train et vous accueille à l’arrivée.",
      "Mise à disposition ou dépose le long de la Corniche d’Or, reprise sur appel.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Saint-Raphaël"),
    {
      q: "Combien de temps pour l’aéroport de Nice ?",
      a: "Comptez environ 1 heure par l’A8. Saint-Raphaël étant à l’ouest de Nice, ce transfert est plus long que depuis Cannes : nous prévoyons une marge et suivons votre vol.",
    },
    {
      q: "Assurez-vous les navettes depuis la gare TGV ?",
      a: "Oui. Nous prenons en charge à la gare de Saint-Raphaël-Valescure et assurons la liaison vers votre hôtel, une villa ou une autre ville du golfe et du Var.",
    },
    {
      q: "Desservez-vous l’Estérel et les criques d’Agay ?",
      a: "Oui, la Corniche d’Or, le Cap Dramont, Agay et Anthéor font partie de nos trajets, avec dépose aux plages, clubs de plongée et départs de sentiers.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en été et pendant les ponts ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et les bagages.",
    },
  ],
  relatedLinks: [
    {
      label: "Réserver un chauffeur à Saint-Tropez",
      href: "/chauffeur/saint-tropez",
    },
    {
      label: "Mise à disposition avec chauffeur à Cannes",
      href: "/chauffeur/cannes",
    },
    {
      label: "Chauffeur Côte d’Azur, tarifs et trajets",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Louer une voiture Saint-Raphaël",
      href: "/location/location-voiture-saint-raphael",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Saint-Raphaël",
    text: "Transfert aéroport de Nice, navette gare TGV ou mise à disposition dans l’Estérel : réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Saint-Raphaël 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Saint-Raphaël : transferts aéroport de Nice (~1 h), navettes gare TGV, Corniche d’Or et Estérel. 5,0/5 sur 38 avis. Devis immédiat.",
    canonical: "/chauffeur/saint-raphael",
  },
};

export default saintRaphael;
